import { extractQuantitativeClaims, extractSuperlatives } from './anatomy';
import { checkProseSlop } from './slop';

import { AD_SPECS, type AdPlatformId } from '@/constants/ad-specs';

/**
 * Would a competent paid-social operator let this ad go live?
 *
 * ── WHY THIS EXISTS, WHEN SO MUCH DOCTRINE ALREADY DOES ─────────────────────
 * `ad-copy-doctrine.ts` teaches a model how to WRITE copy, and `ad-contract.ts`
 * checks a platform's structural limits (lengths, counts, required fields). Both
 * are about the copy object. Neither has ever looked at the ASSEMBLED ad — copy
 * plus creative plus destination plus audience plus objective — and asked whether
 * the thing as a whole is worth spending money on.
 *
 * That gap is not theoretical. The first complete Meta hierarchy Mora ever created
 * (2026-09-02, ad `52599503376027`) went out with NO IMAGE: its own preview said
 * "Add media to complete this layout preview" and every structural check passed,
 * because no check owned the question "is there a creative at all". A rubric that
 * only ever sees the copy cannot catch an ad that has no picture.
 *
 * ── FIXED CRITERIA, DYNAMIC THRESHOLDS ──────────────────────────────────────
 * The founder asked whether the bar should be set in stone or vary per customer.
 * Both, and the split matters:
 *
 *   The CRITERIA are fixed, in code, versioned. "Does the destination match the
 *   promise" is not a matter of taste, and a standard each workspace redefines is
 *   not a standard — it is a preference, and it cannot be regression-tested.
 *
 *   The FACTS the criteria are evaluated against are per-workspace and arrive as
 *   `AdReadinessContext`: the brand's own domain, the claims it can evidence, its
 *   banned words. Same rubric, different evidence.
 *
 * ── SEVERITY DISCIPLINE IS INHERITED, NOT REINVENTED ────────────────────────
 * `content-doctrine`'s rule holds here unchanged and is the reason this is safe
 * to gate on: **a check may only `fail` when it is objective. Anything requiring
 * taste is capped at `warn`, forever.** "There is no image" is objective and can
 * block. "This hook is boring" is taste and never can, no matter how confident a
 * model sounds. This is stricter than anything in the captured competitor corpus,
 * and it is what stops a quality gate becoming an opinion that blocks real work.
 */

export type AdReadinessSeverity = 'fail' | 'warn';

export type AdReadinessCheckId =
  | 'creative_present'
  | 'destination_present'
  | 'destination_matches_brand'
  | 'promise_matches_destination'
  | 'audience_assumes_no_prior_contact'
  | 'claims_are_evidenced'
  | 'no_invented_brand_mark'
  | 'outcome_is_measurable'
  | 'cta_present'
  | 'corny_em_dash_present'
  | 'corny_self_referential_narration'
  | 'corny_hook_too_long'
  | 'corny_claim_missing_mechanism'
  | 'corny_description_too_long'
  | 'corny_cta_mismatched_to_temperature'
  | 'creative_promise_echoed';

/**
 * The six ANTI-CORNY checks (`ad-copy-doctrine.ts`'s `ANTI_CORNY_RULES`),
 * broken out so a caller that only wants copy-craft findings — not the full
 * assembled-ad report — can filter to exactly these. `copy-assistant.ts`'s
 * `evaluateAdCopyAntiCorny` is that caller: it runs the full
 * `checkAdReadiness` report against a synthetic ad (real copy, placeholder
 * creative/destination) and keeps only the ids in this list, so the two
 * "encode them as checks" locations the doctrine promises share one
 * implementation instead of drifting into two.
 */
export const ANTI_CORNY_CHECK_IDS: readonly AdReadinessCheckId[] = [
  'corny_em_dash_present',
  'corny_self_referential_narration',
  'corny_hook_too_long',
  'corny_claim_missing_mechanism',
  'corny_description_too_long',
  'corny_cta_mismatched_to_temperature',
];

export interface AdReadinessFinding {
  id: AdReadinessCheckId;
  severity: AdReadinessSeverity;
  /** What is wrong, in the operator's words. No jargon, no rule numbers. */
  detail: string;
  /** The single next move. Never "review this". */
  fix: string;
}

/** The assembled ad, as it would actually run. */
export interface AdUnderReview {
  primaryText: string;
  headline: string;
  description?: string;
  cta?: string | null;
  /** Provider media identifier, or null when the ad carries no creative. */
  creativeRef?: string | null;
  /** Where a click lands. */
  destinationUrl?: string | null;
  /** `prospecting` means the reader has never heard of this brand. */
  audienceMode?: 'prospecting' | 'warm' | 'existing_customers' | 'advantage_plus' | null;
  objective?: string | null;
  /**
   * What the reader actually sees on the creative this copy runs beside, in
   * the labeller's/generator's own words — the same field
   * `generateGroundedAdCopy` accepts. Optional: most ads reviewed here have
   * no description on file, and absence is UNKNOWN, never a failure.
   */
  creativeDescription?: string | null;
  /**
   * Which provider this ad ships to. Defaults to `'meta'` — every existing
   * caller (`copy-assistant.ts`, `generate-grounded-ad-copy.ts`,
   * `meta-authoring-view-model.ts`) is Meta-only and never set this field, so
   * the default preserves their behavior exactly. A LinkedIn caller passes
   * `'linkedin'` to grade the ad against LinkedIn's own field limits and CTA
   * model instead of Meta's — see `AD_READINESS_PLATFORM_PROFILES` below.
   */
  platform?: AdPlatformId;
}

/** Per-workspace facts. Same rubric, different evidence. */
export interface AdReadinessContext {
  /** Domains this brand legitimately owns. Empty means "unknown", never "none". */
  brandDomains?: readonly string[];
  /** Claims the workspace can actually support, from the Brief's evidence. */
  evidenceOnFile?: readonly string[];
  /** The brand's real name, for the invented-wordmark check. */
  brandName?: string | null;
  /** Whether outcomes from this destination can be attributed at all. */
  conversionTrackingReady?: boolean;
}

export interface AdReadinessReport {
  findings: readonly AdReadinessFinding[];
  /** True when nothing objective is broken. Taste warnings never block. */
  launchable: boolean;
}

/** Words that promise a relationship a cold reader does not have. */
const PRIOR_CONTACT_PHRASES = [
  'as you know',
  'back in stock',
  'welcome back',
  'as always',
  'again',
  'your favourite',
  'your favorite',
  'still thinking about',
  'left in your cart',
];

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return null;
  }
}

/** Words a reader would recognise as naming a place, product or number. */
function specificTokens(text: string): string[] {
  return [...extractQuantitativeClaims(text), ...extractSuperlatives(text)];
}

/** Common connective words — never a "promise" the copy needs to echo. */
const CREATIVE_PROMISE_STOPWORDS = new Set([
  'this',
  'that',
  'with',
  'from',
  'your',
  'their',
  'shows',
  'showing',
  'visible',
  'text',
  'reads',
  'image',
  'photo',
  'video',
  'scene',
  'shot',
]);

const CREATIVE_PROMISE_QUOTED_PATTERN = /["“']([^"”']{4,80})["”']/g;
/** A run of 2+ ALL-CAPS words — the shape on-image copy is usually described in. */
const CREATIVE_PROMISE_CAPS_PATTERN = /\b[A-Z0-9][A-Z0-9 ,'-]{3,80}[A-Z0-9]\b/g;

/**
 * The specific words/phrases a creative promises — its quoted or ALL-CAPS
 * visible on-image text, or a fallback of nothing when the description reads
 * as plain lowercase scene narration with no quotable promise in it.
 */
export function extractCreativePromisePhrases(creativeDescription: string): readonly string[] {
  const text = creativeDescription.trim();
  if (!text) return [];
  const phrases = new Set<string>();
  for (const match of text.matchAll(CREATIVE_PROMISE_QUOTED_PATTERN)) {
    const phrase = match[1]?.trim();
    if (phrase) phrases.add(phrase);
  }
  for (const match of text.matchAll(CREATIVE_PROMISE_CAPS_PATTERN)) {
    const phrase = match[0]?.trim();
    if (phrase && phrase.split(/\s+/).length >= 2) phrases.add(phrase);
  }
  return [...phrases];
}

function creativePromiseWords(creativeDescription: string): string[] {
  const words = new Set<string>();
  for (const phrase of extractCreativePromisePhrases(creativeDescription)) {
    for (const word of phrase.toLowerCase().split(/[^a-z0-9]+/)) {
      if (word.length >= 4 && !CREATIVE_PROMISE_STOPWORDS.has(word)) words.add(word);
    }
  }
  return [...words];
}

/**
 * Does the hook or headline echo at least one word from what the creative
 * actually promises? The incident this exists for (2026-09-02, Mora's own
 * "A MONTH OF CONTENT IN ONE AFTERNOON" Meta creative): every candidate hook
 * ignored the creative's own on-image promise. Nothing extractable from the
 * description is UNKNOWN, never a failure — `echoed: true` when there is
 * nothing to check against, per this file's absent-context discipline.
 */
export function checkCreativePromiseEcho(
  creativeDescription: string | null | undefined,
  hook: string,
  headline: string,
): { echoed: boolean; checkedWords: readonly string[] } {
  const words = creativeDescription ? creativePromiseWords(creativeDescription) : [];
  if (words.length === 0) return { echoed: true, checkedWords: [] };
  const haystack = `${hook} ${headline}`.toLowerCase();
  return { echoed: words.some((word) => haystack.includes(word)), checkedWords: words };
}

/**
 * The hook — the first sentence of the primary text, or the whole text when
 * it carries no terminal punctuation. This is what a skimmer reads before
 * deciding whether the rest is worth "See more".
 */
export function extractAdCopyHook(primaryText: string): string {
  const match = /^[^.!?]*[.!?]/.exec(primaryText.trim());
  return (match ? match[0] : primaryText).trim();
}

/**
 * The product narrating its own internal process AT the reader, instead of
 * naming what the reader gets. This is the exact shape of the incident this
 * section exists for: "Mora reads your website... so the first draft already
 * sounds like you." A regex, not a model call — the tell is a fixed set of
 * internal-process verbs paired with "your", or the specific "sounds like
 * you" resolution those verbs lead to.
 */
const SELF_REFERENTIAL_NARRATION_PATTERN =
  /\b(?:reads?|scans?|analy[sz]es?|studies|learns?|understands?|builds?|generates?|writes?)\s+(?:in\s+)?your\s+(?:website|storefront|store|site|brand|data|content|catalog(?:ue)?|voice|business|shop)\b|\balready\s+sounds?\s+like\s+you\b|\bfirst\s+draft\s+already\b/i;

const FIRST_PERSON_SUBJECT = /^(?:we|our|i|my|us)\b/i;

const READER_RECEIVES =
  /\b(?:gives?|sends?|delivers?|hands?|shows?|tells?)\s+you\b|\byou(?:['’]ll|\s+will)?\s+(?:get|receive|keep|save|walk\s+away|leave\s+with|see)\b/i;

/**
 * A caption written entirely about the brand: every sentence opens on "We", "Our" or the brand's
 * own name ("ITC Tax reports a client cut its tax. We surface opportunities."), and none says
 * what the reader gets. ITC run 11: a caption like that passed "Names what the reader gets"
 * because the only check was a list of internal-process verbs. One sentence about the reader's
 * situation, or one that says what the reader receives, is enough to pass: this is not a ban on
 * naming the brand.
 */
export function brandLedWithoutReaderOutcome(
  primaryText: string,
  brandName: string | null | undefined,
): string | null {
  const sentences = primaryText
    .split(/\n+/)
    .flatMap((paragraph) => paragraph.split(/(?<=[.!?])\s+(?=[A-Z0-9"'“(])/))
    .map((sentence) => sentence.trim())
    .filter(Boolean);
  if (sentences.length < 2) return null;
  const brand = brandName?.trim().toLowerCase() ?? '';
  const brandLed = (sentence: string): boolean =>
    FIRST_PERSON_SUBJECT.test(sentence) ||
    (brand.length > 0 && sentence.toLowerCase().startsWith(brand));
  if (!sentences.every(brandLed)) return null;
  if (sentences.some((sentence) => READER_RECEIVES.test(sentence))) return null;
  return sentences.find((sentence) => FIRST_PERSON_SUBJECT.test(sentence)) ?? sentences[0]!;
}

/** Case/whitespace-insensitive normalize, for verbatim-phrase comparison. */
function normalizePhrase(text: string): string {
  return text.toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * The clause the narration pattern matched inside — from the previous
 * sentence terminator (or the start of the field) to the next one (or the
 * end) — trimmed. Not the whole ad: just enough surrounding context to judge
 * whether the specific claim the model wrote is the brand's own citable one.
 */
function extractClauseAround(text: string, matchStart: number, matchEnd: number): string {
  const before = text.slice(0, matchStart);
  const clauseStart =
    Math.max(before.lastIndexOf('.'), before.lastIndexOf('!'), before.lastIndexOf('?')) + 1;
  const after = text.slice(matchEnd);
  const terminator = /[.!?]/.exec(after);
  const clauseEnd = terminator ? matchEnd + terminator.index + 1 : text.length;
  return text.slice(clauseStart, clauseEnd).trim();
}

/**
 * A citable USP is not narration. `SELF_REFERENTIAL_NARRATION_PATTERN` bans
 * "reads your storefront" as the product narrating its own process — but
 * that string is Mora's own sourced USP verbatim, the one differentiator a
 * competitor cannot also claim, and the ban does not know the difference
 * between a fabricated internal-process flourish and a citation of evidence
 * the caller already authorized (2026-09-03: the ceiling round's best
 * candidate hit this exact collision). Exempt when the clause the narration
 * sits inside is, word-for-word (case/whitespace aside), EITHER contained in
 * an evidence entry (the model quoted the whole clause, or an excerpt of a
 * longer sourced sentence) OR itself contains an evidence entry that covers
 * the MAJORITY of the clause's own words (the model wrapped the citation in
 * a short connective lead-in — "You regain your afternoon because Mora
 * reads your storefront..." — without inventing a claim of its own). Both
 * directions require the evidence phrase to be at least four words, and the
 * containing-clause direction additionally requires it to be a minority
 * partner, never the coincidental floor doing the work alone.
 *
 * 2026-09-03 FIX ROUND: `normalizedClause.includes(normalizedClaim)` used to
 * fire on its own, unconditioned on how much of the clause the evidence
 * actually covered — so ANY clause containing a >=4-word evidence fragment
 * anywhere inside it was exempted, no matter how much fabricated narration
 * surrounded it. That is a length coincidence dressed up as an anti-gaming
 * floor, not a real citation check, and it is not hypothetical:
 * `collectAdClaims` legitimately emits short evidence entries ("Selected
 * catalog product type: hoodie.", "Selected catalog price: $29.00.") that a
 * model could stitch onto an otherwise-invented sentence and still clear the
 * old bare-containment test. The majority-coverage requirement below keeps
 * the legitimate wrapped-citation case working while rejecting exactly that
 * gaming shape — see `ad-readiness.test.ts`'s "does NOT exempt a long,
 * mostly-invented clause" guard.
 */
function isNarrationCitedAsEvidence(clause: string, evidenceOnFile: readonly string[]): boolean {
  const clauseWordCount = clause.split(/\s+/).filter(Boolean).length;
  if (clauseWordCount < 4) return false;
  const normalizedClause = normalizePhrase(clause);
  if (!normalizedClause) return false;
  const normalizedClauseWordCount = normalizedClause.split(' ').filter(Boolean).length;
  return evidenceOnFile.some((claim) => {
    const normalizedClaim = normalizePhrase(claim);
    // The evidence PHRASE that does the exempting must itself be >=4 words —
    // otherwise a short, coincidental fragment on file ("reads your
    // storefront", no more) would exempt any sentence that happens to
    // contain it, regardless of what the rest of that sentence claims.
    const claimWordCount = normalizedClaim.split(' ').filter(Boolean).length;
    if (!normalizedClaim || claimWordCount < 4) return false;
    // Direction 1: the evidence on file IS (or contains, verbatim) the
    // model's whole clause — a full or excerpted citation.
    if (normalizedClaim.includes(normalizedClause)) return true;
    // Direction 2: the model's clause contains the evidence phrase, wrapped
    // in its own connective words — allowed ONLY when the cited evidence is
    // the majority of the clause's words, so a short evidence fragment can
    // license a short lead-in but never license paragraphs of invention
    // built around it.
    if (normalizedClause.includes(normalizedClaim)) {
      return claimWordCount * 2 > normalizedClauseWordCount;
    }
    return false;
  });
}

/** A claim followed by an unfinished thought is not a mechanism. */
const MECHANISM_CONNECTOR_PATTERN =
  /\b(?:because|so (?:that|you|your|it|they)|since|which means|thanks to|by \w+ing)\b/i;

/** Ends the copy on a trailing thought instead of landing the sentence. */
const TRAILING_ELLIPSIS_PATTERN = /(?:\.{3}|…)\s*$/;

/** Meta CTAs that ask for a purchase-grade commitment from a cold reader. */
const HIGH_COMMITMENT_CTAS = new Set([
  'shop_now',
  'order_now',
  'get_offer',
  'subscribe',
  'sign_up',
  'book_now',
  'book_travel',
  'apply_now',
]);
const COLD_APPROPRIATE_CTAS = new Set(['learn_more', 'watch_more', 'contact_us', 'get_quote']);

/**
 * Per-provider grading facts, keyed off `AdPlatformId` (`@/constants/ad-specs`)
 * so a new provider is a new Record entry, not a new branch — see
 * `provider-union-ratchet.guard.test.ts`, which fails the build if this file
 * grows a fourth `provider === 'meta' ? … : …` two-provider ternary instead.
 *
 * ── WHY EACH FIELD EXISTS ────────────────────────────────────────────────
 * `ctaInCopy` — Meta's ad copy carries a `cta` field the API renders as the
 * button. LinkedIn's does not: LinkedIn renders its CTA as its own button
 * chosen from a fixed provider list, never from the copy object
 * (`ad-copy-doctrine.ts`'s `LINKEDIN_CRAFT_RULES`: "Do not write a call to
 * action into the copy... LinkedIn renders the CTA as its own short button
 * chosen from a fixed list"; `platform-limits.ts` says the same). So
 * `cta_present` — "this ad has no call to action" — is true for a bare Meta
 * ad and FALSE FOR EVERY CORRECT LINKEDIN AD, because a correct LinkedIn ad
 * never has one in this field. The check must not run at all for a provider
 * where `ctaInCopy` is false; it is not-applicable, not passed.
 *
 * `hookAdviseMax` / `descriptionAdviseMax` — the same advisory ceilings
 * `ad-contract.ts` already reads from `AD_SPECS`, reused here rather than
 * reinvented. LinkedIn's numbers (150 / 100) are the platform-doc values
 * verified 2026-08-11 against LinkedIn's own "Single image ads advertising
 * specifications" help page — see `AD_SPECS.linkedin` for the citation.
 *
 * `ctaTemperature` — the enum sets `HIGH_COMMITMENT_CTAS`/
 * `COLD_APPROPRIATE_CTAS` are Meta's own snake_case CTA enum
 * (`shop_now`, `learn_more`, …). No LinkedIn CTA-button enum has been
 * verified against LinkedIn's own docs anywhere in this repo yet, and this
 * file does not invent one — omitted (`undefined`) means the temperature
 * check does not run for that provider, which is the honest "we have no
 * verified rule for this yet" state, not a silent Meta-shaped grading of a
 * LinkedIn value that happens never to match.
 */
interface AdReadinessPlatformProfile {
  /** Human-facing name for finding text, e.g. "LinkedIn's ~150-character…". */
  label: string;
  ctaInCopy: boolean;
  hookAdviseMax: number | null;
  descriptionAdviseMax: number | null;
  ctaTemperature?: {
    highCommitment: ReadonlySet<string>;
    coldAppropriate: ReadonlySet<string>;
  };
}

const AD_READINESS_PLATFORM_PROFILES: Record<AdPlatformId, AdReadinessPlatformProfile> = {
  meta: {
    label: 'Meta',
    ctaInCopy: true,
    hookAdviseMax: AD_SPECS.meta.hookFold.adviseMax,
    descriptionAdviseMax: AD_SPECS.meta.description.adviseMax,
    ctaTemperature: {
      highCommitment: HIGH_COMMITMENT_CTAS,
      coldAppropriate: COLD_APPROPRIATE_CTAS,
    },
  },
  linkedin: {
    label: 'LinkedIn',
    ctaInCopy: false,
    hookAdviseMax: AD_SPECS.linkedin.commentary.adviseMax,
    descriptionAdviseMax: AD_SPECS.linkedin.description.adviseMax,
    // No verified LinkedIn CTA-button enum on file — see doc comment above.
  },
  google_rsa: {
    label: 'Google',
    // Not modeled by this checker yet: Google RSA has no single "primary
    // text"/"description" pair this file's hook/description checks assume
    // (it is headlines[] + descriptions[]), and no caller passes
    // `platform: 'google_rsa'` today. Kept conservative (not-applicable
    // everywhere) rather than guessed, so adding the entry costs nothing to
    // any real caller and invents no behavior for one that doesn't exist yet.
    ctaInCopy: false,
    hookAdviseMax: null,
    descriptionAdviseMax: null,
  },
};

/**
 * Grades an assembled ad.
 *
 * Absent context is treated as UNKNOWN and never as a failure: a missing brand
 * domain list means "we cannot check the destination", not "the destination is
 * wrong". Refusing to judge is the honest output when the evidence is missing,
 * and it is the difference between a gate and an obstacle.
 */
export function checkAdReadiness(
  ad: AdUnderReview,
  context: AdReadinessContext = {},
): AdReadinessReport {
  const findings: AdReadinessFinding[] = [];
  const add = (
    id: AdReadinessCheckId,
    severity: AdReadinessSeverity,
    detail: string,
    fix: string,
  ) => findings.push({ id, severity, detail, fix });

  const platform: AdPlatformId = ad.platform ?? 'meta';
  const platformProfile = AD_READINESS_PLATFORM_PROFILES[platform];

  // ── Objective, and therefore blocking ────────────────────────────────────
  if (!ad.creativeRef) {
    add(
      'creative_present',
      'fail',
      'This ad has no image or video. It would run as text against a blank space.',
      'Attach a creative, or generate one from the Concept before launching.',
    );
  }

  if (!ad.destinationUrl) {
    add(
      'destination_present',
      'fail',
      'This ad has nowhere to send a click.',
      'Set the destination URL on the ad.',
    );
  }

  // Not applicable on a provider where the CTA is not a copy field at all
  // (LinkedIn renders its own button from a fixed list — `ctaInCopy: false`
  // in `AD_READINESS_PLATFORM_PROFILES`). This check simply does not run
  // there; a correct LinkedIn ad with no `cta` value is never reported as
  // passing OR failing it, because it was never evaluated.
  if (platformProfile.ctaInCopy && !ad.cta) {
    add(
      'cta_present',
      'fail',
      'This ad has no call to action, so the click has no stated purpose.',
      'Choose a call to action that matches the destination.',
    );
  }

  const host = ad.destinationUrl ? hostOf(ad.destinationUrl) : null;
  if (ad.destinationUrl && !host) {
    add(
      'destination_present',
      'fail',
      'The destination is not a valid URL, so the ad cannot be clicked through.',
      'Fix the destination URL.',
    );
  }

  const domains = (context.brandDomains ?? [])
    .map((d) =>
      d
        .trim()
        .toLowerCase()
        .replace(/^www\./, ''),
    )
    .filter((d) => d !== '');
  if (host && domains.length > 0) {
    const owned = domains.some((bare) => host === bare || host.endsWith(`.${bare}`));
    if (!owned) {
      add(
        'destination_matches_brand',
        'fail',
        `This ad sends clicks to ${host}, which is not one of this brand's domains.`,
        'Point the ad at a page this brand owns, or add the domain to the brand kit if it is legitimate.',
      );
    }
  }

  const brand = context.brandName?.trim();
  if (brand) {
    // The invented-wordmark failure has already reached a real draft once.
    const copy = `${ad.primaryText} ${ad.headline} ${ad.description ?? ''}`;
    const mentionsOtherMark = /\b([A-Z][a-z]+ (?:Coffee|Roasters|Studios|Labs|Co\.))\b/.exec(copy);
    if (mentionsOtherMark && !mentionsOtherMark[1].toLowerCase().includes(brand.toLowerCase())) {
      add(
        'no_invented_brand_mark',
        'fail',
        `The copy names "${mentionsOtherMark[1]}", which is not this brand.`,
        `Use ${brand}, or remove the name.`,
      );
    }
  }

  // ── Taste-adjacent, and therefore capped at warn ─────────────────────────
  if (ad.audienceMode === 'prospecting' || ad.audienceMode === 'advantage_plus') {
    const copy = `${ad.primaryText} ${ad.headline}`.toLowerCase();
    const hit = PRIOR_CONTACT_PHRASES.find((phrase) => copy.includes(phrase));
    if (hit) {
      add(
        'audience_assumes_no_prior_contact',
        'warn',
        `The copy says "${hit}", but this audience has never heard of you.`,
        'Write for a first impression, or move this ad to a warm audience.',
      );
    }
  }

  const claims = specificTokens(`${ad.primaryText} ${ad.headline} ${ad.description ?? ''}`);
  const evidence = (context.evidenceOnFile ?? []).join(' ').toLowerCase();
  const unevidenced = claims.filter((claim) => !evidence.includes(claim.toLowerCase()));
  if (claims.length > 0 && unevidenced.length > 0) {
    add(
      'claims_are_evidenced',
      'warn',
      `Nothing on file supports ${unevidenced
        .slice(0, 3)
        .map((c) => `"${c}"`)
        .join(', ')}.`,
      'Add the source to the Brief, or soften the claim to what you can show.',
    );
  }

  if (context.conversionTrackingReady === false) {
    add(
      'outcome_is_measurable',
      'warn',
      'Nothing will attribute a sale to this ad, so its result will be unknowable.',
      'Connect conversion tracking for this destination, or accept that this ad is a reach test.',
    );
  }

  if (ad.creativeDescription) {
    const hook = extractAdCopyHook(ad.primaryText);
    const { echoed, checkedWords } = checkCreativePromiseEcho(
      ad.creativeDescription,
      hook,
      ad.headline,
    );
    if (!echoed) {
      add(
        'creative_promise_echoed',
        'warn',
        `The creative promises "${checkedWords.slice(0, 3).join('", "')}", but neither the hook nor the headline mentions it.`,
        'Write the hook or headline FROM the creative — echo its actual promise, not a different angle written in parallel with it.',
      );
    }
  }

  if (host && ad.primaryText) {
    // A promise the destination does not obviously serve is the most common
    // reason a click bounces. Only checkable crudely, so it warns.
    const brandWordInHost = host.split('.')[0];
    const promisesSomethingElse =
      brandWordInHost.length > 3 &&
      !ad.primaryText.toLowerCase().includes(brandWordInHost) &&
      !ad.headline.toLowerCase().includes(brandWordInHost);
    if (promisesSomethingElse) {
      add(
        'promise_matches_destination',
        'warn',
        `The copy never mentions ${brandWordInHost}, but that is where the click lands.`,
        'Name the destination in the copy, or send the click somewhere that matches the promise.',
      );
    }
  }

  // ── ANTI-CORNY — countable copy-craft gates (`ad-copy-doctrine.ts`) ──────
  // Only the em-dash rule is a hard house style rule (`slop.ts`, already
  // enforced elsewhere as a block) and fails here; the rest stay warnings —
  // they are countable, but judging whether a claim "has a mechanism" or a
  // narration pattern is corny still carries enough interpretation that this
  // file's own severity discipline puts them with taste, not assembly.
  const slop = checkProseSlop(ad.primaryText, { emDashBudget: 0 });
  if (slop.emDashes > 0) {
    add(
      'corny_em_dash_present',
      'fail',
      `The primary text uses ${slop.emDashes} em ${slop.emDashes === 1 ? 'dash' : 'dashes'}. Mora's own house rule is none in ad copy, ever.`,
      'Rewrite with a comma, a period, or a colon instead of the em dash.',
    );
  }

  const cornyCopy = `${ad.primaryText} ${ad.headline} ${ad.description ?? ''}`;
  const narrationHit = SELF_REFERENTIAL_NARRATION_PATTERN.exec(cornyCopy);
  const narrationClause = narrationHit
    ? extractClauseAround(
        cornyCopy,
        narrationHit.index,
        narrationHit.index + narrationHit[0].length,
      )
    : null;
  if (
    narrationHit &&
    narrationClause &&
    isNarrationCitedAsEvidence(narrationClause, context.evidenceOnFile ?? [])
  ) {
    // Citable, not corny — see `isNarrationCitedAsEvidence`. No finding.
  } else if (narrationHit) {
    add(
      'corny_self_referential_narration',
      'warn',
      `"${narrationHit[0]}" narrates the product's own process instead of naming what the reader gets.`,
      'Rewrite around the outcome for the reader, not the steps the product takes to produce it.',
    );
  } else if (brandLedWithoutReaderOutcome(ad.primaryText, context.brandName)) {
    const sentence = brandLedWithoutReaderOutcome(ad.primaryText, context.brandName);
    add(
      'corny_self_referential_narration',
      'warn',
      `Every sentence of the intro is about the brand ("${sentence}"), and none says what the reader gets.`,
      'Keep the brand-led proof line, and add a sentence about the reader: what they receive, or the situation they are in.',
    );
  } else if (TRAILING_ELLIPSIS_PATTERN.test(ad.primaryText.trim())) {
    add(
      'corny_self_referential_narration',
      'warn',
      'The copy trails off on an ellipsis instead of landing the sentence.',
      'Finish the thought. A trailing "..." reads as a flourish, not an idea.',
    );
  }

  const hook = extractAdCopyHook(ad.primaryText);
  const hookAdviseMax = platformProfile.hookAdviseMax;
  if (hookAdviseMax && hook.length > hookAdviseMax) {
    if (platform === 'linkedin') {
      add(
        'corny_hook_too_long',
        'warn',
        `The first sentence runs ${hook.length} characters. LinkedIn shows about ${hookAdviseMax} before “…see more”, so the hook may not be visible until someone taps. The intro itself can run to ${AD_SPECS.linkedin.commentary.hardMax?.toLocaleString('en-US') ?? '3,000'} characters.`,
        `Put the outcome or tension inside the first ~${hookAdviseMax} characters. A longer intro is fine; this is a note, not a defect.`,
      );
    } else {
      add(
        'corny_hook_too_long',
        'warn',
        `The hook runs ${hook.length} characters before its first full stop, over ${platformProfile.label}'s ~${hookAdviseMax}-character advised length before it gets cut or hidden.`,
        'Cut the hook to a single outcome or tension that lands inside the advised length; move the rest into the body.',
      );
    }
  }

  if (ad.primaryText.trim().length > 60 && !MECHANISM_CONNECTOR_PATTERN.test(ad.primaryText)) {
    add(
      'corny_claim_missing_mechanism',
      'warn',
      'No claim in the primary text says how it works or why it is true.',
      'Attach the mechanism to the claim in the same sentence, or shorten to something with no claim to support.',
    );
  }

  const descriptionAdviseMax = platformProfile.descriptionAdviseMax;
  if (ad.description && descriptionAdviseMax && ad.description.length > descriptionAdviseMax) {
    add(
      'corny_description_too_long',
      'warn',
      `The description is ${ad.description.length} characters; ${platformProfile.label} renders roughly ${descriptionAdviseMax} before it is cut.`,
      'Cut the description to a short bonus line — an offer, a guarantee, one proof point — never load-bearing.',
    );
  }

  // CTAs on this ad may arrive as a provider enum ("shop_now") or as the
  // label a human typed ("Shop Now") — normalize both to the same
  // snake_case token before matching, the same way
  // `copy-suggestions-core.ts`'s `normalizeMetaCta` reconciles the two
  // shapes. The classification sets themselves (`ctaTemperature`) are
  // per-provider — `platformProfile.ctaTemperature` is `undefined` for a
  // provider with no verified CTA enum on file, and the check does not run
  // there rather than grading that provider's values against Meta's enum
  // (the defect this replaces: LinkedIn CTA values silently never matched
  // Meta's `shop_now`/`learn_more` sets, so the check quietly never fired).
  const ctaTemperature = platformProfile.ctaTemperature;
  const cta =
    ad.cta
      ?.trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '') ?? null;
  if (
    ctaTemperature &&
    cta &&
    (ad.audienceMode === 'prospecting' || ad.audienceMode === 'advantage_plus') &&
    ctaTemperature.highCommitment.has(cta) &&
    !ctaTemperature.coldAppropriate.has(cta)
  ) {
    add(
      'corny_cta_mismatched_to_temperature',
      'warn',
      `The CTA "${ad.cta}" asks for a purchase-grade commitment from an audience that has never heard of this brand.`,
      'Use a low-commitment CTA (Learn More, Watch More, Contact Us) for a prospecting or Advantage+ audience.',
    );
  }

  return {
    findings,
    launchable: !findings.some((finding) => finding.severity === 'fail'),
  };
}
